import React, { useState } from 'react';
import {
    Send, CheckCircle, Loader2, MessageSquare
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import { supabase } from '../lib/supabase';

const ContactForm = ({ title, className }) => {
    const { language } = useLanguage();
    const { showModal } = useNotification();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const { error } = await supabase
                .from('contact_messages')
                .insert(formData);

            if (error) throw error;

            showModal(
                language === 'tr' ? 'Başarılı' : 'Success',
                language === 'tr' ? 'Mesajınız başarıyla iletildi. En kısa sürede size geri dönüş yapacağız.' : 'Your message has been sent successfully. We will get back to you soon.',
                'success'
            );

            setFormData({
                full_name: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            });
        } catch (err) {
            console.error('Error submitting message:', err);
            showModal(
                language === 'tr' ? 'Hata' : 'Error',
                language === 'tr' ? 'Mesaj gönderilemedi. Lütfen tekrar deneyin.' : 'Failed to send message. Please try again.',
                'error'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const texts = {
        tr: {
            fullName: 'Ad Soyad',
            email: 'E-posta',
            phone: 'Telefon (Opsiyonel)',
            subject: 'Konu',
            message: 'Mesajınız',
            submit: 'Mesajı Gönder',
            submitting: 'Gönderiliyor...',
            select: 'Seçiniz',
            subjects: [
                'Genel Bilgi',
                'Teklif Talebi',
                'Destek',
                'İş Birliği',
                'Diğer'
            ],
            success: {
                title: 'Mesajınız Alındı!',
                message: 'En kısa sürede size geri dönüş yapacağız. Teşekkürler!',
                button: 'Yeni Mesaj Gönder'
            }
        },
        en: {
            fullName: 'Full Name',
            email: 'Email',
            phone: 'Phone (Optional)',
            subject: 'Subject',
            message: 'Your Message',
            submit: 'Send Message',
            submitting: 'Sending...',
            select: 'Select',
            subjects: [
                'General Inquiry',
                'Quote Request',
                'Support',
                'Partnership',
                'Other'
            ],
            success: {
                title: 'Message Received!',
                message: "We'll get back to you as soon as possible. Thank you!",
                button: 'Send Another Message'
            }
        }
    };

    const t = texts[language] || texts.tr;

    if (submitted) {
        return (
            <div className={`text-center py-12 ${className || ''}`}>
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    {t.success.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-8">
                    {t.success.message}
                </p>
                <button
                    onClick={() => setSubmitted(false)}
                    className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                >
                    <MessageSquare className="w-5 h-5 mr-2" />
                    {t.success.button}
                </button>
            </div>
        );
    }

    return (
        <div className={className}>
            {title && (
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                    {title}
                </h2>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t.fullName} *
                        </label>
                        <input
                            type="text"
                            required
                            value={formData.full_name}
                            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t.email} *
                        </label>
                        <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t.phone}
                        </label>
                        <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t.subject} *
                        </label>
                        <select
                            required
                            value={formData.subject}
                            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                            <option value="">{t.select}</option>
                            {t.subjects.map((subject, idx) => (
                                <option key={idx} value={subject}>{subject}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t.message} *
                    </label>
                    <textarea
                        required
                        rows={6}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                    />
                </div>

                <button
                    type="submit"
                    disabled={submitting}
                    className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-xl font-bold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    {submitting ? (
                        <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            {t.submitting}
                        </>
                    ) : (
                        <>
                            <Send className="w-5 h-5" />
                            {t.submit}
                        </>
                    )}
                </button>
            </form>
        </div>
    );
};

export default ContactForm;
